import { LOCALES, SUPPORTED_LOCALES, toLocale, type Locale } from '@zuruflex/i18n';
import type { EmailAudience, EmailTemplateSummary } from '@zuruflex/types';
import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@zuruflex/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

const AUDIENCES: EmailAudience[] = ['CUSTOMER', 'PARTNER', 'STAFF'];

type View = 'html' | 'text';

/**
 * Every transactional email the API sends, rendered exactly as it goes
 * out — same copy, same layout — with sample values in place of a real
 * booking, in any of the three languages.
 *
 * Read-only on purpose. The copy lives in the i18n catalogues and the
 * frame in the API's `htmlEmail`, so this page is where a change to either
 * is checked, not where it is made; and there is no "send a test" button,
 * because a button that emails somebody from an admin page is one somebody
 * eventually presses with a real customer's address in the field.
 */
export function EmailTemplatesPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { key } = useParams<{ key: string }>();
  const [locale, setLocale] = useState<Locale>(() => toLocale(i18n.language));
  const [view, setView] = useState<View>('html');

  const list = useQuery({
    queryKey: ['email-templates', locale],
    queryFn: () => api.emailTemplates(locale),
    placeholderData: keepPreviousData,
  });

  const selectedKey = key ?? list.data?.[0]?.key;

  const preview = useQuery({
    queryKey: ['email-template', selectedKey, locale],
    queryFn: () => api.emailPreview(selectedKey!, locale),
    enabled: Boolean(selectedKey),
    placeholderData: keepPreviousData,
  });

  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('emailTemplates.title')}
        description={t('emailTemplates.description')}
        action={
          <Tabs value={locale} onValueChange={(value) => setLocale(toLocale(value))}>
            <TabsList>
              {SUPPORTED_LOCALES.map((code) => (
                <TabsTrigger key={code} value={code}>
                  {LOCALES[code].nativeName}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(320px,380px)_1fr]">
        <nav aria-label={t('emailTemplates.title')} className="space-y-6">
          {list.isLoading
            ? Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-20 rounded-card" />
              ))
            : AUDIENCES.map((audience) => {
                const rows = (list.data ?? []).filter((row) => row.audience === audience);
                if (rows.length === 0) return null;
                return (
                  <section key={audience} className="space-y-2">
                    <h2 className="px-1 text-[11px] font-bold uppercase tracking-[0.16em] text-text-muted">
                      {t(`emailTemplates.audience.${audience}`)}
                    </h2>
                    <ul className="space-y-1.5">
                      {rows.map((row) => (
                        <li key={row.key}>
                          <TemplateRow row={row} active={row.key === selectedKey} />
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
        </nav>

        <div className="min-w-0 space-y-4">
          {!selectedKey && !list.isLoading ? (
            <EmptyState icon={<Mail className="size-5" />} title={t('emailTemplates.empty')} />
          ) : preview.error ? (
            <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
          ) : (
            <>
              <Card>
                <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
                  <div className="min-w-0 space-y-1">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-text-muted">
                      {t('emailTemplates.subject')}
                    </p>
                    {preview.data ? (
                      <p className="text-lg font-semibold text-text">{preview.data.subject}</p>
                    ) : (
                      <Skeleton className="h-7 w-80" />
                    )}
                    {/* The API's English is the fallback, so a template added
                        there reads before its copy reaches the catalogues. */}
                    <p className="text-sm text-text-muted">
                      {preview.data
                        ? t(`emailTemplates.trigger.${preview.data.key}`, {
                            defaultValue: preview.data.trigger,
                          })
                        : null}
                    </p>
                  </div>
                  <Tabs value={view} onValueChange={(value) => setView(value as View)}>
                    <TabsList>
                      <TabsTrigger value="html">{t('emailTemplates.viewHtml')}</TabsTrigger>
                      <TabsTrigger value="text">{t('emailTemplates.viewText')}</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardContent>
              </Card>

              <div
                className={cn(
                  'overflow-hidden rounded-card border border-border-default transition-opacity',
                  preview.isPlaceholderData && 'opacity-60',
                )}
              >
                {!preview.data ? (
                  <Skeleton className="h-[720px] rounded-none" />
                ) : view === 'html' ? (
                  /*
                    An empty sandbox: the email's own links are inert here
                    and no script in a template could reach the admin. The
                    frame stays white in the dark theme, because the email
                    is light-only wherever it is read.
                  */
                  <iframe
                    title={t('emailTemplates.previewTitle', { key: preview.data.key })}
                    sandbox=""
                    srcDoc={preview.data.html}
                    className="block h-[720px] w-full bg-white"
                  />
                ) : (
                  <pre className="min-h-[320px] whitespace-pre-wrap break-words bg-surface-raised p-6 font-sans text-[15px] leading-relaxed text-text">
                    {preview.data.text}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateRow({ row, active }: { row: EmailTemplateSummary; active: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <Link
      to={`/emails/${encodeURIComponent(row.key)}`}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'block rounded-card border px-4 py-3 transition-colors',
        active
          ? 'border-transparent bg-accent-soft text-accent-soft-text'
          : 'border-border-default bg-surface-raised hover:bg-surface-hover',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className={cn('truncate text-sm font-semibold', active ? '' : 'text-text')}>
          {row.subject}
        </span>
        {!row.hasAction && <Badge variant="default">{t('emailTemplates.noButton')}</Badge>}
      </div>
      <span
        className={cn(
          'mt-0.5 block font-mono text-[11px]',
          active ? 'opacity-80' : 'text-text-subtle',
        )}
      >
        {row.key}
      </span>
    </Link>
  );
}
